import { computed, inject } from '@angular/core';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { type ActionState, ActionStateCreator } from '@ui/legacy-lib';
import {
  type FilterOptions,
  type Pagination,
  type PropertyMatchBean,
  PropertyType
} from '@ui/shared/model';
import { sortCustomDefinedFieldsOnPropertyMatches } from '@ui/tenant-pool/shared/util';
import { RentedPropertyMatchesFacade } from './rented-property-matches.facade';

/**
 * The rented list has no pagination UI, so the filter is a constant rather
 * than a method argument. Same values the legacy `NewHomeGuard` dispatched.
 */
export const RENTED_FILTER: FilterOptions = {
  page: 0,
  sort: 'id,asc',
  size: 100
};

export interface RentedPropertyMatchesState {
  rented: PropertyMatchBean[];
  pagination: Pagination;
  rentedActionState: ActionState;
}

export const initialState: RentedPropertyMatchesState = {
  rented: [],
  pagination: {},
  rentedActionState: ActionStateCreator.create()
};

/**
 * The tenant's rented objects — single source of truth, replacing the
 * `propertyMatchesRented` half of the classic `+state/property-matches` slice.
 *
 * `providedIn: 'root'` is a deliberate exception to the house rule that stores
 * are provided on their feature route. Root-level facades (`ApplicationFacade`,
 * `RentalDepositFacade`) and the classic property-matches effects reload it, and
 * three unrelated feature routes read it, so no single route can own it. It
 * replaces a root NgRx reducer; root scope is the equivalent.
 */
export const RentedPropertyMatchesStore = signalStore(
  { providedIn: 'root' },
  withState<RentedPropertyMatchesState>(initialState),
  withDevtools('rented-property-matches'),

  withComputed(store => ({
    hasRented: computed(() => store.rented().length > 0),

    isPending: computed(() => store.rentedActionState().pending),

    /** Rented objects the moving-auction wizard can act on. */
    rentedExcludingGarages: computed(() =>
      store
        .rented()
        .filter(match => match.property?.type !== PropertyType.GARAGE)
    )
  })),

  withMethods((store, facade = inject(RentedPropertyMatchesFacade)) => {
    const loadRentedProperties = rxMethod<void>(
      pipe(
        tap(() =>
          patchState(store, {
            rentedActionState: ActionStateCreator.onStart()
          })
        ),
        switchMap(() =>
          facade.getRentedPropertyMatches(RENTED_FILTER).pipe(
            tap(({ nodes, page }) =>
              patchState(store, {
                rented: sortCustomDefinedFieldsOnPropertyMatches(nodes ?? []),
                pagination: page ?? {},
                rentedActionState: ActionStateCreator.onSuccess()
              })
            ),
            // No toast — the legacy `LoadPropertyMatchesRentedFail` showed none
            // either; consumers render off the action state.
            catchError((e: unknown) => {
              patchState(store, {
                rented: [],
                rentedActionState: ActionStateCreator.onError(e as Error)
              });
              return EMPTY;
            })
          )
        )
      )
    );

    return {
      loadRentedProperties,

      /**
       * Explicit re-read, replacing `PropertyMatchesFacade.refetchRentedMatches()`.
       * Needed because every read is `fetchPolicy: 'no-cache'`, so there is no
       * Apollo cache entry a mutation could update.
       */
      reload: (): void => {
        loadRentedProperties();
      },

      /** Replaces the `getPropertyMatchRentedData` selector. */
      findRented: (id: string): PropertyMatchBean | undefined =>
        store.rented().find(match => match.id === id)
    };
  })
);
